import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPremiumFeatures, getPremiumStatus, getPrograms } from '../lib/api';
import { LOGO_URL, SKETCH_PATTERNS } from '../lib/images';
import useRazorpay from '../hooks/useRazorpay';
import useCurrency from '../hooks/useCurrency';
import usePromoPreview from '../hooks/usePromoPreview';
import PriceWithPromo from '../components/PriceWithPromo';
import {
  Crown, Check, Star, Loader2, ChevronRight, Shield,
  BarChart3, BookOpen, Brain, Dumbbell, Sparkles, Zap
} from 'lucide-react';

const featureIcons = {
  mood_tracker: BarChart3,
  gratitude_journal: BookOpen,
  personality_test: Brain,
};

export default function PremiumPage() {
  const { user, deviceId } = useAuth();
  const { pay, loading: payLoading, error: payError, authDialog } = useRazorpay();
  const currency = useCurrency();
  const promo = usePromoPreview();
  const [features, setFeatures] = useState(null);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const [justPurchased, setJustPurchased] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [featRes, statusRes, progsRes] = await Promise.allSettled([
          getPremiumFeatures(),
          getPremiumStatus(deviceId),
          getPrograms(),
        ]);
        if (featRes.status === 'fulfilled') {
          setFeatures(featRes.value.data.features || []);
          setPrice(featRes.value.data.price || 2999);
        }
        if (statusRes.status === 'fulfilled') {
          setPremiumStatus(statusRes.value.data);
        }
        // Programs come from the real /api/programs source so the list here
        // always matches what's shown on the Programs page (names, durations,
        // and program IDs).
        if (progsRes.status === 'fulfilled') {
          const data = progsRes.value.data;
          const list = Array.isArray(data) ? data : (data.programs || []);
          const normalized = list.map((p) => ({
            id: p.id || p.program_id,
            name: p.name || p.title,
            duration: typeof p.duration === 'number'
              ? `${p.duration} days`
              : (p.duration || (p.duration_days ? `${p.duration_days} days` : '')),
          }));
          setPrograms(normalized);
        } else if (featRes.status === 'fulfilled' && featRes.value.data.programs) {
          // Fallback only if /api/programs fails
          setPrograms(featRes.value.data.programs);
        }
      } catch {}
      setLoading(false);
    }
    load();
  }, [deviceId]);

  const handlePurchase = () => {
    pay({
      deviceId,
      itemType: 'premium',
      amount: price,
      currency: currency.currency,
      userName: user?.name,
      userEmail: user?.email,
      allowGuest: true,
      onSuccess: async () => {
        setJustPurchased(true);
        try {
          const res = await getPremiumStatus(deviceId);
          setPremiumStatus(res.data);
        } catch {}
      },
    });
  };

  const isPremium = premiumStatus?.is_premium;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="premium-page">
      {authDialog}
      {/* Success State */}
      {justPurchased && (
        <div className="rounded-lg p-6 mb-8 text-center animate-slide-up"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }} data-testid="purchase-success">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-80" />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>Welcome to Premium!</h2>
          <p className="opacity-80">All features and programs are now unlocked. Enjoy your wellness journey!</p>
        </div>
      )}

      {/* Hero */}
      <div className={`rounded-2xl overflow-hidden mb-8 relative py-14 px-8 text-center ${isPremium ? '' : 'keep-light-theme'}`}
        style={{ background: isPremium
          ? 'linear-gradient(135deg, #879B7D 0%, #768A6C 40%, #2C4A3B 100%)'
          : 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)'
        }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '250px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div className="relative z-10">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
            style={{ background: isPremium ? 'rgba(255,255,255,0.2)' : 'var(--accent)', color: 'white' }}>
            <Crown className="w-8 h-8" />
          </div>
          {isPremium ? (
            <>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>You're Premium</h1>
              <p className="text-white/70">All features and programs are unlocked</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Upgrade to Premium</h1>
              <p className="mb-6" style={{ color: 'var(--muted-fg)' }}>Unlock everything for your complete wellness transformation</p>
              <div className="inline-flex items-baseline gap-1 mb-5">
                <PriceWithPromo inrAmount={price} currency={currency} promo={promo} size="xl" testid="premium-price" />
                <span className="text-sm ml-1" style={{ color: 'var(--muted-fg)' }}>one-time</span>
              </div>
              {!currency.isINR && !currency.loading && (
                <p className="text-xs mb-2" style={{ color: 'var(--muted-fg)' }}>
                  Billed in {currency.currency} · INR ₹{promo.valid ? Math.round(promo.apply(price)) : price}
                </p>
              )}
              <br />
              {payError && (
                <p className="text-sm mb-3" style={{ color: '#DC2626' }} data-testid="payment-error">{payError}</p>
              )}
              <button onClick={handlePurchase} disabled={payLoading} data-testid="purchase-premium-btn"
                className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center gap-2"
                style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {payLoading ? 'Processing...' : 'Get Premium Now'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* What's Included */}
      <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        {isPremium ? 'Your Premium Features' : "What's Included"}
      </h2>

      {/* Features */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {(features || []).map((f, i) => {
          const Icon = featureIcons[f.id] || Star;
          const unlocked = isPremium || premiumStatus?.unlocked_features?.[f.id];
          return (
            <div key={f.id} className={`rounded-lg p-5 border animate-fade-in stagger-${i + 1}`}
              style={{
                background: unlocked ? 'var(--primary)' + '08' : 'var(--surface)',
                borderColor: unlocked ? 'var(--primary)' + '40' : 'var(--border)',
              }} data-testid={`feature-${f.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: unlocked ? 'var(--primary)' : 'var(--muted)', color: unlocked ? 'var(--primary-fg)' : 'var(--muted-fg)' }}>
                  <Icon className="w-5 h-5" />
                </div>
                {unlocked && <Check className="w-4 h-4" style={{ color: 'var(--primary)' }} />}
              </div>
              <h3 className="font-bold text-sm mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{f.name}</h3>
              <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{f.description}</p>
            </div>
          );
        })}
      </div>

      {/* Premium Programs */}
      {programs.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            {isPremium ? 'Your Premium Programs' : 'Premium Programs'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {programs.map((p, i) => {
              const unlocked = isPremium || (premiumStatus?.unlocked_programs || []).includes(p.id);
              return (
                <div key={p.id} className={`rounded-lg p-4 border flex items-center gap-3 animate-fade-in stagger-${(i % 6) + 1}`}
                  style={{
                    background: unlocked ? 'var(--primary)' + '08' : 'var(--surface)',
                    borderColor: unlocked ? 'var(--primary)' + '40' : 'var(--border)',
                  }} data-testid={`program-${p.id}`}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: unlocked ? 'var(--primary)' : 'var(--muted)', color: unlocked ? 'var(--primary-fg)' : 'var(--muted-fg)' }}>
                    <Dumbbell className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate" style={{ color: 'var(--text)' }}>{p.name}</p>
                    <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{p.duration}</p>
                  </div>
                  {unlocked && <Check className="w-4 h-4 shrink-0" style={{ color: 'var(--primary)' }} />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trust badges */}
      <div className="rounded-lg p-5 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted-fg)' }}>
            <Shield className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            Secure Payment via Razorpay
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted-fg)' }}>
            <Crown className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            Lifetime Access
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted-fg)' }}>
            <Star className="w-4 h-4" style={{ color: '#FFD93D' }} />
            All Future Updates
          </div>
        </div>
      </div>
    </div>
  );
}
